import { useState, useEffect } from 'react';
import adminApi from '@/lib/adminApi';
import { toast } from 'sonner';
import { Music, Plus, Trash2, Save, Loader2, GripVertical, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';


export const MusicThumbnailsTab = ({ onRefresh }) => {
  const [thumbnails, setThumbnails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchThumbnails();
  }, []);

  const fetchThumbnails = async () => {
    try {
      const res = await adminApi.get(`/music-thumbnails`);
      setThumbnails(res.data);
    } catch (error) {
      console.error('Error fetching music thumbnails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (thumbnails.length >= 4) {
      toast.error('Máximo 4 thumbnails permitidos');
      return;
    }

    try {
      const res = await adminApi.post(`/music-thumbnails`, {
        image_url: '',
        link_url: '',
        title: '',
        order: thumbnails.length
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      setThumbnails([...thumbnails, res.data]);
      toast.success('Thumbnail agregado');
    } catch (error) {
      toast.error('Error al agregar thumbnail');
    }
  };

  const handleUpdate = async (id, field, value) => {
    // Update locally first
    setThumbnails(thumbnails.map(t => 
      t.id === id ? { ...t, [field]: value } : t
    ));
  };

  const handleSave = async (thumb) => {
    setSaving(true);
    try {
      await adminApi.put(`/music-thumbnails/${thumb.id}`, {
        image_url: thumb.image_url,
        link_url: thumb.link_url,
        title: thumb.title,
        order: thumb.order
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      toast.success('Cambios guardados');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este thumbnail?')) return;

    try {
      await adminApi.delete(`/music-thumbnails/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      setThumbnails(thumbnails.filter(t => t.id !== id));
      toast.success('Thumbnail eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Music className="text-[#E91E8C]" size={24} />
          <h2 className="text-xl font-bold text-black">Thumbnails de Música (Homepage)</h2>
        </div>
        <Button
          onClick={handleAdd}
          disabled={thumbnails.length >= 4}
          className="bg-[#E91E8C] hover:bg-[#E91E8C]/80 text-white"
        >
          <Plus className="mr-2" size={16} />
          Agregar ({thumbnails.length}/4)
        </Button>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-700">
          Estas imágenes aparecen en el módulo de <strong>Música</strong> en la página principal. 
          Usa imágenes cuadradas (ej: 500x500 px) para mejores resultados. Máximo 4 thumbnails.
        </p>
      </div>

      {/* Thumbnails Grid */}
      {thumbnails.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
          <Music className="mx-auto text-gray-300 mb-4" size={48} />
          <p className="text-gray-500">No hay thumbnails configurados</p>
          <p className="text-gray-400 text-sm mt-1">Agrega imágenes de álbumes o sencillos</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {thumbnails.map((thumb, index) => (
            <div 
              key={thumb.id}
              className="bg-white border border-gray-200 rounded-xl p-4 space-y-4"
            >
              {/* Preview */}
              <div className="flex gap-4">
                <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                  {thumb.image_url ? (
                    <img 
                      src={thumb.image_url} 
                      alt={thumb.title || `Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="text-gray-300" size={32} />
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <GripVertical className="text-gray-400" size={16} />
                    <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                  </div>
                  <Input
                    type="text"
                    value={thumb.title || ''}
                    onChange={(e) => handleUpdate(thumb.id, 'title', e.target.value)}
                    placeholder="Título (opcional)"
                    className="bg-gray-50 border-gray-200 text-sm"
                  />
                </div>
              </div>

              {/* Image URL */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500 uppercase">URL de Imagen</label>
                <Input
                  type="url"
                  value={thumb.image_url || ''}
                  onChange={(e) => handleUpdate(thumb.id, 'image_url', e.target.value)}
                  placeholder="https://ejemplo.com/album.jpg"
                  className="bg-gray-50 border-gray-200"
                />
              </div>

              {/* Link URL */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
                  <ExternalLink size={12} />
                  URL de Enlace (al hacer clic)
                </label>
                <Input
                  type="url"
                  value={thumb.link_url || ''}
                  onChange={(e) => handleUpdate(thumb.id, 'link_url', e.target.value)}
                  placeholder="https://open.spotify.com/album/..."
                  className="bg-gray-50 border-gray-200"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-2 border-t border-gray-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(thumb.id)}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={16} className="mr-1" />
                  Eliminar
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleSave(thumb)}
                  disabled={saving}
                  className="bg-[#E91E8C] hover:bg-[#E91E8C]/80 text-white"
                >
                  {saving ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <>
                      <Save size={16} className="mr-1" />
                      Guardar
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
